"use client";

import "@/components/home/features-crawl.css";
import { FigureCaptionLine } from "@/components/home/figure-caption";
import { SectionKicker } from "@/components/home/section-kicker";
import { Button } from "@/components/ui/shadcn/button";
import { memo, useRef, useState } from "react";

/**
 * The complete capability inventory, grounded in
 * docs/brand/feature-inventory-2026-07-05.md (code-verified). Every line
 * exists in the product; add here only after the inventory doc confirms it.
 */
const CATEGORIES: {
    name: string;
    items: string[];
}[] = [
    {
        name: "Reports & analysis",
        items: [
            "One-click report import",
            "SmartCredit & Credit Karma integrations",
            "Side-by-side bureau view",
            "Cross-bureau discrepancy analyzer",
            "Report history timeline",
            "Round-over-round compare",
            "Historical score back-fill",
        ],
    },
    {
        name: "The dispute engine",
        items: [
            "Dispute wizard",
            "48 Metro 2® field checks",
            "§611 / §623 statute routing",
            "Dual-axis selection engine",
            "Escalation ladder",
            "MOV demands",
            "Deletion demands",
            "Reinsertion detection",
            "FCRA 30-day round timers",
            "Automatic outcome grading",
            "Creditor address resolution",
            "Secondary bureaus",
        ],
    },
    {
        name: "Letters & mail",
        items: [
            "20+ statute-cited templates",
            "Round-tone escalation",
            "Custom templates with live tokens",
            "Per-letter edit & regenerate",
            "Packetization",
            "Tracked print & mail",
        ],
    },
    {
        name: "Fluid",
        items: [
            "The Fluid assistant, 60 real tools",
            "Auto-prepare full rounds",
            "Human approval gates",
            "Automation scheduler",
            "Visual workflow builder",
            "Four-layer FCRA/FDCPA analysis",
            "Self-calibrating score model",
        ],
    },
    {
        name: "Contacts & leads",
        items: [
            "Unified contacts hub",
            "Lead pipeline",
            "One-click lead conversion",
            "Public intake capture",
            "5,000-row CSV wizard",
            "Bulk operations",
            "Command palette",
        ],
    },
    {
        name: "Client workspace",
        items: [
            "Client detail workspace",
            "Command center dashboard",
            "Persistable widget grid",
            "Score tracking charts",
            "Activity timeline",
            "Outcome attribution",
        ],
    },
    {
        name: "Communications",
        items: [
            "Global inbox",
            "Two-way SMS",
            "Portal messaging",
            "Drip campaigns",
            "DNS-verified sending identities",
            "Notes",
            "Tasks",
            "Calendar widget",
        ],
    },
    {
        name: "Portal & onboarding",
        items: [
            "Branded client portal",
            "Portal setup gate",
            "Login page designer",
            "Onboarding builder, 11 step types",
            "In-house e-signature",
            "Document center",
            "Virus scanning",
            "Self-serve data export & deletion",
        ],
    },
    {
        name: "Client billing",
        items: [
            "Authorize.net & NMI",
            "Tokenized card storage",
            "Recurring subscriptions",
            "Automatic retry",
            "Invoices",
            "Quick charge",
            "Bulk charge",
            "Portal payments",
            "Charging guardrails",
        ],
    },
    {
        name: "White label",
        items: [
            "BrandKit light & dark",
            "Theme studio",
            "Score-gauge styles",
            "Custom domains with DNS wizard",
            "Naming conventions",
            "Asset library",
        ],
    },
    {
        name: "Team & compliance",
        items: [
            "Five-tier roles",
            "Outsourcer tier",
            "Permission matrix",
            "Tenant isolation",
            "Audit trail",
            "Encryption at rest",
            "CROA agreements with computed deadlines",
            "50-state evidence dashboard",
            "Contract term limits",
            "Progress report PDFs",
            "Credit analysis PDFs",
        ],
    },
    {
        name: "Migration",
        items: [
            "DisputeFox one-click migration",
            "Live migration progress",
            "OTP-resume report pulls",
        ],
    },
];

const TOTAL_ITEMS = CATEGORIES.reduce((sum, cat) => sum + cat.items.length, 0);

/**
 * Section 08 on /features: the joke that carries the inventory. The page
 * admits it ran out of room, then the full itemized list follows in a
 * scrollable stage.
 */
export const FeaturesCrawl = memo(function FeaturesCrawl() {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [playing, setPlaying] = useState(false);

    const toggleTheme = () => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }
        if (audio.paused) {
            void audio.play();
            setPlaying(true);
        } else {
            audio.pause();
            setPlaying(false);
        }
    };

    return (
        <section className="dw-feat-crawl dw-home__stack" aria-label="The index" data-component="FeaturesCrawl" data-location="src/components/home/features-crawl.tsx">
            <SectionKicker numeral="09" label="The Index" note="Cue the music" />
            <h2 className="dw-home__headline dw-text-hero" data-tone="foreground">Moving on&hellip;</h2>

            <figure className="dw-feat-crawl__figure">
                <div className="dw-feat-crawl__caption-row">
                    <FigureCaptionLine variant="bare">
                        {`Fig. 9 · All ${TOTAL_ITEMS} capabilities, in one take`}
                    </FigureCaptionLine>
                    <Button variant="bare" size="auto" type="button" className="dw-feat-crawl__music dw-text-caption dw-tabular-nums" data-tone="foreground" aria-pressed={playing} onClick={toggleTheme}>
                        {playing ? "❚❚ Pause the theme" : "▶ Play the theme"}
                    </Button>
                    <audio ref={audioRef} src="/audio/dw-crawl-theme.mp3" preload="none" onEnded={() => setPlaying(false)} />
                </div>
                <div className="dw-feat-crawl__stage">
                    <div className="dw-feat-crawl__scroll">
                        <p className="dw-text-marketing-display" data-tone="foreground">There are so many features, we&apos;re running out of room on the page&hellip;</p>
                        <p className="dw-text-marketing-display" data-tone="foreground">But&hellip;</p>
                        <div className="dw-feat-crawl__lockup">
                            <p className="dw-text-marketing-display" data-tone="foreground">here they all are.</p>
                            <p className="dw-text-caption dw-tabular-nums" data-tone="muted">
                                {`${TOTAL_ITEMS} capabilities · ${CATEGORIES.length} systems · all shipped`}
                            </p>
                        </div>
                        {CATEGORIES.map((cat) => (
                            <div key={cat.name} className="dw-feat-crawl__block">
                                <h3 className="dw-text-marketing-title" data-tone="foreground">{cat.name}</h3>
                                <p className="dw-text-body dw-tabular-nums" data-tone="muted">{cat.items.join(" · ")}</p>
                            </div>
                        ))}
                        <div className="dw-feat-crawl__lockup">
                            <p className="dw-text-marketing-display" data-tone="foreground">That&apos;s everything.</p>
                            <p className="dw-text-caption dw-tabular-nums" data-tone="muted">There is no more room on this page either.</p>
                        </div>
                    </div>
                </div>
            </figure>
        </section>
    );
});
